import { useState } from "react";
import { Link, useRoute } from "wouter";
import { ArrowLeft, Loader2, Pencil, Star, Store } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/layout/PageHeader";
import { BillingActions } from "@/components/billing/BillingActions";
import { BillingAmount, BillingSituation } from "@/components/billing/BillingCells";
import { BillingStatusBadge } from "@/components/sellers/BillingStatusBadge";
import { SellerEditDialog } from "@/components/sellers/SellerEditDialog";
import { TestPartnerCard } from "@/components/sellers/TestPartnerCard";
import { OrderStatusBadge } from "@/components/dashboard/OrderStatusBadge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { Pagination } from "@/components/ui/pagination";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  useSeller,
  useSellerBilling,
  useSellerBillingStatus,
  useSellerOrders,
  useSellerProducts,
  useSellerRatings,
  useSellerSalesSummary,
  useSetSellerActive,
} from "@/hooks/use-sellers";
import { useCategories } from "@/hooks/use-categories";
import { usePagination } from "@/hooks/use-pagination";
import { isTestPartner } from "@/lib/billing";
import { describeCallableError } from "@/lib/callables";
import {
  formatCnpj,
  formatCurrency,
  formatDate,
  formatDateTime,
  formatMonthYear,
  formatNumber,
  formatPhone,
} from "@/lib/format";
import { productTypeName } from "@/lib/productTypes";
import { productOptionCounts, productPriceRange } from "@/lib/productOptions";
import { orderCode } from "@/lib/orderCode";
import type { Product } from "@/types/firestore";

/** O preço do produto, ou "A partir de" o menor quando as variações custam diferente. */
function priceLabel(product: Product) {
  const range = productPriceRange(product);
  const price = formatCurrency(range?.min ?? product.price);
  return range && range.max > range.min ? `A partir de ${price}` : price;
}

/** " · Combo · 3 variações · 2 adicionais · 1 grupo de adicionais", ou nada. */
function optionsLabel(product: Product) {
  const { variations, addons, addonGroups, compositeItems } = productOptionCounts(product);
  const parts = [
    compositeItems === null
      ? ""
      : product.compositeItemsPerVariation
      ? "Combo, itens por variação"
      : `Combo de ${compositeItems === 1 ? "1 item" : `${compositeItems} itens`}`,
    variations > 0 ? (variations === 1 ? "1 variação" : `${variations} variações`) : "",
    addons > 0 ? (addons === 1 ? "1 adicional" : `${addons} adicionais`) : "",
    addonGroups > 0
      ? addonGroups === 1
        ? "1 grupo de adicionais"
        : `${addonGroups} grupos de adicionais`
      : "",
  ].filter(Boolean);
  return parts.length > 0 ? ` · ${parts.join(" · ")}` : "";
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <p className="text-caption font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-0.5 break-words text-small">{value || "-"}</p>
    </div>
  );
}

export default function SellerDetail() {
  const [, params] = useRoute("/vendedores/:id");
  const id = params?.id;

  const seller = useSeller(id);
  const billingStatus = useSellerBillingStatus(id);
  const billing = useSellerBilling(id);
  const products = useSellerProducts(id);
  const productTypes = useCategories();
  const ratings = useSellerRatings(id);
  const salesSummary = useSellerSalesSummary(id);
  const orders = useSellerOrders(id, salesSummary.data?.totalOrders);
  const billingPage = usePagination(billing.data ?? []);
  const productsPage = usePagination(products.data ?? []);
  const ratingsPage = usePagination(ratings.data ?? []);
  const setActive = useSetSellerActive();
  const [editing, setEditing] = useState(false);

  if (seller.isLoading) {
    return (
      <>
        <Skeleton className="mb-6 h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </>
    );
  }

  if (!seller.data) {
    return (
      <EmptyState
        icon={Store}
        title="Estabelecimento não encontrado"
        description="Ele pode ter sido removido, ou o endereço está errado."
        action={
          <Button asChild variant="outline">
            <Link href="/vendedores">Voltar para a lista</Link>
          </Button>
        }
      />
    );
  }

  const s = seller.data;
  const address = s.address;
  // Para a aba de produtos: o nome do tipo (a busca do cliente) e a categoria do
  // cardápio (a organização do vendedor) de cada produto.
  const typeNames = new Map((productTypes.data ?? []).map((t) => [t.id, t.name]));
  const sectionOfProduct = new Map<string, string>();
  for (const section of s.menuSections ?? []) {
    for (const productId of section.productIds ?? []) {
      sectionOfProduct.set(productId, section.name || "Sem nome");
    }
  }
  const deliveredCount = salesSummary.data?.deliveredOrders ?? 0;
  const lifetimeRevenue = salesSummary.data?.deliveredRevenue ?? 0;

  async function toggleActive(next: boolean) {
    try {
      await setActive.mutateAsync({
        sellerId: s.id,
        isActive: next,
        reason: next ? undefined : "Desativado pelo painel de gestão",
      });
      toast.success(next ? "Estabelecimento reativado." : "Estabelecimento desativado.");
    } catch (error) {
      toast.error(describeCallableError(error));
    }
  }

  return (
    <>
      <Button asChild variant="ghost" size="sm" className="mb-3 -ml-2">
        <Link href="/vendedores">
          <ArrowLeft /> Vendedores
        </Link>
      </Button>

      <PageHeader
        title={s.name || "Sem nome"}
        description={s.description || "Sem descrição cadastrada."}
        actions={
          <Button variant="outline" onClick={() => setEditing(true)}>
            <Pencil /> Corrigir cadastro
          </Button>
        }
      />

      {/* Selos de situação, com ponto: são estado, e não navegação. As abas
          logo abaixo têm outra forma (sublinhadas) para as duas coisas não se
          confundirem. */}
      <div
        className="mb-6 flex flex-wrap items-center gap-2"
        role="group"
        aria-label="Situação do estabelecimento"
      >
        {s.isActive ? (
          <Badge variant="ok" dot>
            Ativo
          </Badge>
        ) : (
          <Badge variant="error" dot>
            Inativo
          </Badge>
        )}
        {s.isOpen ? (
          <Badge variant="default" dot>
            Aberto agora
          </Badge>
        ) : (
          <Badge variant="muted" dot>
            Fechado
          </Badge>
        )}
        {s.isTestSeller ? (
          <Badge variant="muted" dot>
            Vendedor de teste
          </Badge>
        ) : null}
        {isTestPartner(billingStatus.data) ? (
          <Badge variant="ok" dot>
            Isento da taxa (parceiro de teste)
          </Badge>
        ) : null}
        <BillingStatusBadge status={billingStatus.data?.status} dot />
      </div>

      {s.blockReason ? (
        <Card className="mb-4 border-destructive bg-destructive/5">
          <CardContent className="p-4">
            <p className="text-small font-bold text-destructive">Bloqueado pelo faturamento</p>
            <p className="mt-1 text-small text-muted-foreground">
              {s.blockReason}
              {s.blockedAt ? ` Desde ${formatDateTime(s.blockedAt)}.` : ""}
            </p>
            <p className="mt-2 text-caption text-muted-foreground">
              O bloqueio é desfeito pelo backend quando a fatura é paga. Reativar aqui devolve a
              visibilidade, e o estabelecimento continua fechado até o vendedor abri-lo.
            </p>
          </CardContent>
        </Card>
      ) : null}

      <Tabs defaultValue="cadastro">
        <TabsList>
          <TabsTrigger value="cadastro">Cadastro</TabsTrigger>
          <TabsTrigger value="faturas">Faturas</TabsTrigger>
          <TabsTrigger value="vendas">Vendas</TabsTrigger>
          <TabsTrigger value="produtos">Produtos</TabsTrigger>
          <TabsTrigger value="avaliacoes">Avaliações</TabsTrigger>
        </TabsList>

        <TabsContent value="cadastro" className="space-y-3">
          <Card>
            <CardHeader>
              <CardTitle>Dados do estabelecimento</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <Field label="E-mail" value={s.email} />
              <Field label="Telefone" value={formatPhone(s.phone)} />
              <Field label="CNPJ" value={formatCnpj(s.cnpj)} />
              <Field
                label="Endereço"
                value={
                  address?.street
                    ? `${address.street}, ${address.number ?? "s/n"}${address.neighborhood ? ` - ${address.neighborhood}` : ""}`
                    : null
                }
              />
              <Field
                label="Cidade"
                value={address?.city ? `${address.city}${address.state ? `, ${address.state}` : ""}` : null}
              />
              <Field label="Cadastrado em" value={formatDate(s.createdAt)} />
              <Field label="Taxa de entrega" value={s.deliveryFee != null ? formatCurrency(s.deliveryFee) : null} />
              <Field label="Pedido mínimo" value={s.minimumOrder != null ? formatCurrency(s.minimumOrder) : null} />
              <Field
                label="Tempo estimado"
                value={
                  s.estimatedDeliveryTimeInMinutes
                    ? `${s.estimatedDeliveryTimeInMinutes} min de entrega`
                    : null
                }
              />
              <Field
                label="Nota"
                value={
                  s.rating != null && s.totalRatings
                    ? `${s.rating.toFixed(1)} de ${formatNumber(s.totalRatings)} avaliações`
                    : null
                }
              />
              <Field label="Categorias do cardápio" value={formatNumber(s.menuSections?.length ?? 0)} />
              <Field label="Vende pão francês" value={s.sellsFrenchBread?.isAvailable ? "Sim" : "Não"} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Visibilidade</CardTitle>
              <CardDescription>
                Um estabelecimento inativo some do aplicativo do cliente. Isto não abre nem fecha a
                padaria: abrir é decisão de quem está lá dentro.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <label className="flex items-center justify-between gap-4">
                <span className="min-w-0 text-small font-semibold">
                  Visível no aplicativo do cliente
                </span>
                <span className="flex shrink-0 items-center gap-2">
                  {setActive.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
                  <Switch
                    checked={Boolean(s.isActive)}
                    disabled={setActive.isPending}
                    onCheckedChange={(next) => void toggleActive(next)}
                  />
                </span>
              </label>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="faturas" className="space-y-3">
          <TestPartnerCard
            sellerId={s.id}
            status={billingStatus.data}
            loading={billingStatus.isLoading}
          />

          <Card>
            <CardHeader>
              <CardTitle>Faturas</CardTitle>
              <CardDescription>
                Geradas pelo backend, todo dia às 03:00. O menu de cada fatura dá baixa manual,
                ignora a fatura, volta a cobrar, reenvia por e-mail e recalcula.
                {billingStatus.data?.dueDay
                  ? ` Vencimento no dia ${billingStatus.data.dueDay} de cada mês.`
                  : ""}
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0 sm:p-0">
              {billing.isLoading ? (
                <div className="space-y-2 p-4">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} className="h-10 w-full" />
                  ))}
                </div>
              ) : (billing.data ?? []).length === 0 ? (
                <p className="p-4 text-small text-muted-foreground">
                  Nenhuma fatura gerada para este estabelecimento.
                </p>
              ) : (
                <Table className="table-stacked">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Competência</TableHead>
                      <TableHead className="hidden md:table-cell">Receita</TableHead>
                      <TableHead>Valor</TableHead>
                      <TableHead className="hidden md:table-cell">Vencimento</TableHead>
                      <TableHead>Situação</TableHead>
                      <TableHead className="w-10" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {billingPage.pageItems.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell className="cell-title font-semibold">
                          {row.month && row.year ? formatMonthYear(row.month, row.year) : row.id}
                          {row.accumulatedIntoBillingId ? (
                            <p className="text-caption text-muted-foreground">
                              Acumulada em outra fatura
                            </p>
                          ) : null}
                        </TableCell>
                        <TableCell className="hidden md:table-cell tabular-nums">
                          {formatCurrency(row.totalRevenue)}
                          <span className="block text-caption text-muted-foreground">
                            {formatNumber(row.deliveredOrdersCount)} pedidos
                          </span>
                        </TableCell>
                        <TableCell className="tabular-nums font-semibold">
                          <BillingAmount billing={row} />
                        </TableCell>
                        <TableCell className="hidden md:table-cell">{formatDate(row.dueDate)}</TableCell>
                        <TableCell>
                          <BillingSituation billing={row} />
                        </TableCell>
                        <TableCell className="cell-actions">
                          <BillingActions billing={row} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              <Pagination {...billingPage.paginationProps} itemLabel="faturas" />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="vendas" className="space-y-3">
          <div className="grid grid-cols-1 gap-3 xs:grid-cols-3">
            <Card>
              <CardContent className="p-4">
                <p className="text-caption uppercase tracking-wide text-muted-foreground">Entregues</p>
                <p className="text-h5 font-black">{formatNumber(deliveredCount)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-caption uppercase tracking-wide text-muted-foreground">Receita entregue</p>
                <p className="text-h5 font-black">{formatCurrency(lifetimeRevenue)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-caption uppercase tracking-wide text-muted-foreground">Ticket médio</p>
                <p className="text-h5 font-black">
                  {formatCurrency(deliveredCount ? lifetimeRevenue / deliveredCount : 0)}
                </p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Pedidos</CardTitle>
              <CardDescription>
                Do mais recente para o mais antigo. Os números acima contam todos os pedidos do
                estabelecimento, e não só os desta página.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0 sm:p-0">
              {orders.isLoading ? (
                <div className="space-y-2 p-4">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} className="h-10 w-full" />
                  ))}
                </div>
              ) : orders.rows.length === 0 ? (
                <p className="p-4 text-small text-muted-foreground">Nenhum pedido registrado.</p>
              ) : (
                <Table className="table-stacked">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Pedido</TableHead>
                      <TableHead className="hidden md:table-cell">Data</TableHead>
                      <TableHead>Total</TableHead>
                      <TableHead>Situação</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orders.rows.map((order) => (
                      <TableRow key={order.id}>
                        <TableCell className="cell-title font-mono text-caption">
                          {orderCode(order.id)}
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          {formatDateTime(order.createdAt)}
                        </TableCell>
                        <TableCell className="tabular-nums font-semibold">
                          {formatCurrency(order.total)}
                        </TableCell>
                        <TableCell>
                          <OrderStatusBadge status={order.status} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              <Pagination {...orders.paginationProps} itemLabel="pedidos" />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="produtos">
          <Card>
            <CardHeader>
              <CardTitle>Produtos</CardTitle>
              <CardDescription>
                O catálogo é do vendedor, e as categorias do cardápio também. O painel mostra para conferência, e
                não edita.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0 sm:p-0">
              {products.isLoading ? (
                <div className="space-y-2 p-4">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} className="h-10 w-full" />
                  ))}
                </div>
              ) : (products.data ?? []).length === 0 ? (
                <p className="p-4 text-small text-muted-foreground">Nenhum produto cadastrado.</p>
              ) : (
                <Table className="table-stacked">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Produto</TableHead>
                      <TableHead>Preço</TableHead>
                      <TableHead className="hidden md:table-cell">Modalidade</TableHead>
                      <TableHead>Situação</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {productsPage.pageItems.map((product) => (
                      <TableRow key={product.id}>
                        <TableCell className="cell-title">
                          <span className="font-semibold">{product.name || "Sem nome"}</span>
                          <p className="text-caption text-muted-foreground">
                            Tipo: {productTypeName(product.category, typeNames)}
                            {" · "}
                            {sectionOfProduct.get(product.id) ?? "Sem categoria"}
                            {optionsLabel(product)}
                          </p>
                        </TableCell>
                        <TableCell className="tabular-nums">
                          {priceLabel(product)}
                          {product.saleType === "kilo" ? (
                            <span className="text-caption text-muted-foreground"> por kg</span>
                          ) : null}
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          {product.isReserveItem ? "Encomenda" : "Pronta-entrega"}
                        </TableCell>
                        <TableCell>
                          {product.isAvailable ? (
                            <Badge variant="ok">Disponível</Badge>
                          ) : (
                            <Badge variant="muted">Indisponível</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              <Pagination {...productsPage.paginationProps} itemLabel="produtos" />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="avaliacoes">
          <Card>
            <CardHeader>
              <CardTitle>Avaliações</CardTitle>
              <CardDescription>
                Escritas pelos clientes. A média em `sellers.rating` é recalculada pelo aplicativo do
                cliente, e não pelo backend.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {ratings.isLoading ? (
                <div className="space-y-2">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : (ratings.data ?? []).length === 0 ? (
                <p className="py-4 text-small text-muted-foreground">Nenhuma avaliação recebida.</p>
              ) : (
                <ul className="space-y-3">
                  {ratingsPage.pageItems.map((rating) => (
                    <li key={rating.id} className="rounded-md border border-border p-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="flex items-center gap-1 text-small font-bold">
                          <Star className="size-3.5 fill-state-warning text-state-warning" />
                          {rating.rating ?? "-"}
                        </span>
                        {rating.isGuest ? <Badge variant="muted">Convidado</Badge> : null}
                        <span className="text-caption text-muted-foreground">
                          {formatDate(rating.createdAt)}
                        </span>
                      </div>
                      {rating.comment ? (
                        <p className="mt-1.5 break-words text-small">{rating.comment}</p>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
              <Pagination {...ratingsPage.paginationProps} itemLabel="avaliações" className="-mx-4 mt-3 sm:-mx-5" />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {editing ? <SellerEditDialog seller={s} open={editing} onOpenChange={setEditing} /> : null}
    </>
  );
}
